// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * COB-507 — the **verifier-shared** origin map: the independent verifier's
 * shared-vault review site (`{origin}/v/{vault_id}`).
 *
 * This is the shared-vault sibling of the three yield-site copies:
 *
 *   - `verifier-embed.ts::verifierOriginForNetwork` (the embed host),
 *   - `pages/sign-verifier-challenge.tsx::VERIFIER_ORIGINS_BY_NETWORK`
 *     (the popup return-origin allow-list),
 *   - `pages/vault-detail.tsx::verifierVaultUrlForNetwork` (the deep link).
 *
 * Those three are separate copies cross-checked by a lockstep test. This
 * module deliberately does NOT repeat that: the shared site has exactly one
 * map, and both consumers — the `flow=shared` popup's return-origin check and
 * the shared-vault detail page's review link — read it, so the link a
 * participant follows and the origin we will postMessage a proof to can never
 * drift apart. `tests/verifier-embed.test.ts` pins the per-network literals
 * and the negatives.
 *
 * Hard-coded on purpose, exactly as the yield copies are: a typo'd env var
 * must never widen the trust boundary. Exact string equality only — never a
 * regex over a domain suffix. When a new environment hostname lands, add an
 * explicit entry and a matching test.
 */

/**
 * bitcoin network (user-app's spelling, as served by `/version`) →
 * verifier-shared origin.
 *
 * **One origin per network, unlike the yield allow-list.** The yield map
 * grants `signet` a second entry (`https://app.verifier.cobault.io`) because
 * the yield verifier SPA predates the M-series split and its legacy hostname
 * is still live alongside `verifier.staging.cobault.io`. verifier-shared has
 * no such legacy host — it was born on the M-series names — so a second entry
 * here would widen the boundary for nothing. Staging and production stay
 * strictly non-crossing: a signet (staging) session can never postMessage a
 * proof to the production review site, or vice versa.
 */
const VERIFIER_SHARED_ORIGIN_BY_NETWORK: Readonly<Record<string, string>> = {
  // Dev: the verifier repo serves the shared bundle on :5175 (`npm run
  // dev:shared`); :5174 is the yield bundle.
  regtest: 'http://localhost:5175',
  // M-series staging runs on signet.
  signet: 'https://verifier-shared.staging.cobault.io',
  // Public production networks.
  testnet: 'https://verifier-shared.cobault.io',
  bitcoin: 'https://verifier-shared.cobault.io',
};

/**
 * The verifier-shared origin for a given bitcoin network, or `null` for an
 * unknown/absent network — callers then suppress the link (and the popup
 * refuses the request) rather than pointing at a wrong host. Fails closed.
 */
export function verifierSharedOriginForNetwork(
  network: string | null,
): string | null {
  if (network === null) return null;
  return VERIFIER_SHARED_ORIGIN_BY_NETWORK[network] ?? null;
}

/**
 * `true` iff `origin` is exactly the verifier-shared origin permitted for
 * `network`. The security-critical half of the `flow=shared` popup: it decides
 * where a participant-ownership proof may be posted. Unknown networks and
 * unknown origins return `false` (fail closed); there is no `*` target and no
 * suffix matching.
 */
export function isAllowedSharedReturnOrigin(
  origin: string,
  network: string,
): boolean {
  const allowed = verifierSharedOriginForNetwork(network);
  return allowed !== null && allowed === origin;
}

/**
 * The participant review URL for a shared vault — `{origin}/v/{vault_id}` on
 * the verifier-shared site, or `null` when the network has no mapped origin.
 * The vault id is URL-encoded so a malformed id can never alter the path
 * structure (same rule as `embedConfirmUrl`).
 */
export function verifierSharedVaultUrlForNetwork(
  network: string | null,
  vaultId: string,
): string | null {
  const origin = verifierSharedOriginForNetwork(network);
  if (origin === null) return null;
  return `${origin}/v/${encodeURIComponent(vaultId)}`;
}
